import React from 'react';
import styles from './index.less';
import { IGridLineManager } from '@src/slide/application/service/grid-line-manager/index.interface';
import { reaction } from 'mobx';
import { IReactionDisposer } from 'mobx/lib/internal';
import GridLineDialog, {
    gridSpaceList,
    ONE_CM,
    ONE_EIGHT,
    ONE_FIFTH,
    ONE_FOURTH,
    ONE_SECOND,
    ONE_SIXTH,
    ONE_THIRD,
} from '../grid-line-dialog';
import { IGuidesManager } from '@src/slide/application/service/guides-manager/index.interface';
import { SLIDE_CM_TO_PX } from '@src/slide/infrastructure/module/independent/unit';

export interface IBaseGridLineProps {
    scale: number;
    slideWidth: number;
    slideHeight: number;
    width: number;
    height: number;
    left: number;
    top: number;
}

export interface IGridLineProps extends IBaseGridLineProps {
    gridLineManager: IGridLineManager;
    guidesManager: IGuidesManager;
}

// 默认点长度
const DEFAULT_DOT_LENGTH = 1;
// 默认单元格长度
const DEFAULT_CELL_LENGTH = 2;
// 默认点宽度
const DOT_SCALE = 2;
// 不同长度单元格需要放大的页面scale
const ONE_NEED_SCALE = 0.2;
const ONE_SECOND_NEED_SCALE = 0.4;
const ONE_THIRD_NEED_SCALE = 0.6;
const ONE_FOURTH_NEED_SCALE = 0.8;
const ONE_FIFTH_NEED_SCALE = 1;
const ONE_SIXTH_NEED_SCALE = 1.2;
const ONE_EIGHTH_NEED_SCALE = 1.6;
// 不同长度单元格需要缩小的页面scale
const ONE_NEED_SMALLER_SCALE = 0.3;
const ONE_THIRD_NEED_SMALLER_SCALE = 0.31;
const ONE_FOURTH_NEED_SMALLER_SCALE = 0.42;
const ONE_FIFTH_NEED_SMALLER_SCALE = 0.53;
const ONE_SIXTH_NEED_SMALLER_SCALE = 0.64;
const ONE_EIGHTH_NEED_SMALLER_SCALE = 0.85;

/**
 * 网格线ui
 */
export default class GridLine extends React.Component<IGridLineProps, any> {
    private readonly gridLineManager: IGridLineManager;
    private readonly reaction: IReactionDisposer;

    public constructor(props: IGridLineProps) {
        super(props);
        const { gridLineManager } = props;
        this.gridLineManager = gridLineManager;

        this.reaction = reaction(() => [
            this.gridLineManager.displayGridLine,
            this.gridLineManager.gridSpace,
        ], () => {
            this.forceUpdate();
        });
    }

    componentWillUnmount() {
        this.reaction();
    }

    render() {
        const { slideWidth, slideHeight, scale, width, height, left, top } = this.props;
        this.gridLineManager.setCanvasSize({
            slideWidth, slideHeight, width, height, left, top, scale,
        });
        const { gridSpace } = this.gridLineManager;
        if (!gridSpace) return null;

        const isCustomGrid = this.isCustomGridSpace(gridSpace);
        let cellLength = gridSpace * SLIDE_CM_TO_PX;
        let cellNumber = 0;
        const dotLength = DEFAULT_DOT_LENGTH / scale;
        if (isCustomGrid) {
            // 自定义间距网格的单元格大小，是间距的整倍，非自定义间距的单元格则是固定长度
            const cellCm = this.computeCellLengthWithCustomGridSpace(gridSpace);
            cellLength = cellCm * SLIDE_CM_TO_PX;
            cellNumber = Math.round(cellCm / gridSpace);
        }

        let dotMargin = cellLength;
        if (gridSpace <= 1) {
            if (isCustomGrid) {
                dotMargin = (cellLength - (dotLength * cellNumber)) / cellNumber;
            } else {
                // 1cm以下的非自定义间距间距，大网格固定2cm
                cellLength = DEFAULT_CELL_LENGTH * SLIDE_CM_TO_PX;
                let scaleGridSpace;

                // 不同scale下，不同单元格间距有不同ui
                if (this.isSmallerScaleGrid(gridSpace, scale * 2)) {
                    // 缩小页面后，网格间距放大两倍
                    scaleGridSpace = gridSpace;
                    if (scaleGridSpace === ONE_FIFTH) scaleGridSpace = ONE_FOURTH;
                    scaleGridSpace = scaleGridSpace * 2;
                } else if (this.isScaleGrid(gridSpace, scale * 2)) {
                    scaleGridSpace = gridSpace;
                } else {
                    // 放大页面后，网格间距缩小两倍
                    scaleGridSpace = gridSpace / 2;
                }

                dotMargin = (cellLength - (dotLength / scaleGridSpace)) * scaleGridSpace;
            }
        }

        const { svgHoriCellNumber, svgVertCellNumber } = this.getSvgContainerSize(slideWidth, cellLength, slideHeight);
        return <div style={{
            width: Math.round(width / scale),
            height: Math.round(height / scale),
            transformOrigin: 'left top',
            transform: `scale(${scale})`,
            position: 'absolute',
            pointerEvents: 'none',
            mixBlendMode: 'difference',
        }}>
            <div style={{
                position: 'absolute',
                width: slideWidth, // Hardcoded to 1100:850 aspect ratio
                height: slideHeight,
                top: Math.round(top / scale),
                left: Math.round(left / scale),
            }}
            >
                <div className={styles.gridLineContainer}>
                    {
                        this.gridLineManager.displayGridLine && <div className={styles.gridLineSvgContainer} style={{
                            width: svgHoriCellNumber * cellLength,
                            height: svgVertCellNumber * cellLength,
                        }}>
                            <svg width="100%" height="100%">
                                <defs>
                                    <pattern
                                        id="grid"
                                        width={cellLength}
                                        height={cellLength}
                                        patternUnits="userSpaceOnUse">
                                        <path
                                            d={`M 0 0 L 0 ${cellLength} M 0 0 L ${cellLength} 0`}
                                            fill="none"
                                            stroke={'rgb(167, 167, 167)'}
                                            strokeWidth={dotLength * DOT_SCALE}
                                            strokeDasharray={`${dotLength},${dotMargin}`}
                                        />
                                    </pattern>
                                </defs>
                                <rect width="100%" height="100%" fill="url(#grid)" />
                            </svg>
                        </div>
                    }
                    <GridLineDialog
                        gridLineManager={this.gridLineManager}
                        guidesManager={this.props.guidesManager}
                    />
                </div>
            </div>
        </div>;
    }

    /**
     * 计算svg容器尺寸，相对与slide需要给予一个左右上下冗余，用于居中处理
     * @param slideWidth
     * @param cellLength
     * @param slideHeight
     * @private
     */
    private getSvgContainerSize(slideWidth: number, cellLength: number, slideHeight: number) {
        let svgHoriCellNumber = Math.ceil(slideWidth / cellLength) + 1;
        let svgVertCellNumber = Math.ceil(slideHeight / cellLength) + 1;

        // 网格布局是双数居中的，如果最小单元格是单数，就加1
        if (svgHoriCellNumber % 2 === 1) {
            svgHoriCellNumber += 1;
        }

        if (svgVertCellNumber % 2 === 1) {
            svgVertCellNumber += 1;
        }
        return { svgHoriCellNumber, svgVertCellNumber };
    }

    /**
     * 大缩放比例下，是否需要缩小网格渲染
     * @param gridSpace
     * @param scale
     * @private
     */
    // eslint-disable-next-line complexity
    private isScaleGrid(gridSpace: number, scale: number): boolean {
        if ((gridSpace <= ONE_CM && gridSpace > ONE_SECOND) && scale <= ONE_NEED_SCALE) {
            return true;
        } if ((gridSpace <= ONE_SECOND && gridSpace > ONE_THIRD) && scale <= ONE_SECOND_NEED_SCALE) {
            return true;
        } if ((gridSpace <= ONE_THIRD && gridSpace > ONE_FOURTH) && scale <= ONE_THIRD_NEED_SCALE) {
            return true;
        } if ((gridSpace <= ONE_FOURTH && gridSpace > ONE_FIFTH) && scale <= ONE_FOURTH_NEED_SCALE) {
            return true;
        } if ((gridSpace <= ONE_FIFTH && gridSpace > ONE_SIXTH) && scale <= ONE_FIFTH_NEED_SCALE) {
            return true;
        } if ((gridSpace <= ONE_SIXTH && gridSpace > ONE_EIGHT) && scale <= ONE_SIXTH_NEED_SCALE) {
            return true;
        } if (gridSpace === ONE_EIGHT && scale <= ONE_EIGHTH_NEED_SCALE) {
            return true;
        }
        return false;
    }

    /**
     * 小缩放比例下，是否要放大网格渲染
     * @param gridSpace
     * @param scale
     * @private
     */
    // eslint-disable-next-line complexity
    private isSmallerScaleGrid(gridSpace: number, scale: number): boolean {
        if ((gridSpace <= ONE_CM && gridSpace > ONE_THIRD) && scale < ONE_NEED_SMALLER_SCALE) {
            return true;
        } if ((gridSpace <= ONE_THIRD && gridSpace > ONE_FOURTH) && scale < ONE_THIRD_NEED_SMALLER_SCALE) {
            return true;
        } if ((gridSpace <= ONE_FOURTH && gridSpace > ONE_FIFTH) && scale < ONE_FOURTH_NEED_SMALLER_SCALE) {
            return true;
        } if ((gridSpace <= ONE_FIFTH && gridSpace > ONE_SIXTH) && scale < ONE_FIFTH_NEED_SMALLER_SCALE) {
            return true;
        } if ((gridSpace <= ONE_SIXTH && gridSpace > ONE_EIGHT) && scale < ONE_SIXTH_NEED_SMALLER_SCALE) {
            return true;
        } if (gridSpace === ONE_EIGHT && scale < ONE_EIGHTH_NEED_SMALLER_SCALE) {
            return true;
        }
        return false;
    }

    /**
     * 计算是否是一个自定义间距
     * @param gridSpace
     * @private
     */
    private isCustomGridSpace(gridSpace: number): boolean {
        return gridSpaceList.findIndex(item => item.value === gridSpace) === -1;
    }

    /**
     * 计算自定义间距的单元格长度
     * 取最接近2的长度
     * @param gridSpace
     * @private
     */
    private computeCellLengthWithCustomGridSpace(gridSpace: number): number {
        if (gridSpace > 1) {
            return gridSpace;
        }

        let index = 0;
        let res = 0;
        let min = Number.MAX_SAFE_INTEGER;
        while (index < DEFAULT_CELL_LENGTH + 1) {
            index += gridSpace;
            if (Math.abs(index - DEFAULT_CELL_LENGTH) < min) {
                min = Math.abs(index - DEFAULT_CELL_LENGTH);
                res = index;
            }
        }
        return res;
    }
}
